/**
 * RaceResultsHud — post-race results overlay shown in the post-race lobby.
 *
 * Displays the final standings (sorted by place). PERSISTENT (E.4, Event
 * Control Round A): reads the shared lastEnded store — populated by every
 * gmEventEnded including the server's join-time re-send, so results survive
 * page reloads and late joins. Stays up until dismissed (✕) or until the
 * NEXT race stages (the store clears itself). Standings come from the ended
 * wire's own scoreboard (NOT the separate gmEventScoreboard broadcast, which
 * a reloading client never received).
 *
 * Footer: 🏠 Leave to Lobby — the same exit as the floating RaceHubReturn.
 *
 * Only visible when:
 *   - The current map is gm_race_post_lobby
 *   - The lastEnded store holds results
 *
 * @see docs/design/gm/EVENT_CONTROL_SPEC.md §3.4
 */

import React from 'react';
import { useLastEnded, clearLastEnded } from '../../../services/GmEventService';
import { leaveToHub } from '../../HUD/RaceHubReturn';
import { COLORS, FONTS } from '../../theme';

function getCurrentMapId(): string {
  return (globalThis as any).nm?.getRoom?.()?.state?.mapId ?? '';
}

export function formatRaceTime(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const tenths = Math.floor((ms % 1000) / 100);
  return mins > 0
    ? `${mins}:${secs.toString().padStart(2, '0')}.${tenths}`
    : `${secs}.${tenths}s`;
}

export const RaceResultsHud: React.FC = () => {
  const lastEnded = useLastEnded();

  // Only show on the post-race lobby map.
  const mapId = getCurrentMapId();
  if (!mapId.startsWith('gm_race_post_lobby')) return null;
  if (!lastEnded) return null;

  const entries = lastEnded.scoreboard?.entries ?? [];
  // Sort by place (race scoring) — entries with place first, then by score.
  const sorted = [...entries].sort((a, b) => {
    const aPlace = (a as any).place ?? 999;
    const bPlace = (b as any).place ?? 999;
    if (aPlace !== bPlace) return aPlace - bPlace;
    return b.score - a.score;
  });

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    top: 60,
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '20px 32px',
    background: COLORS.surface,
    border: `2px solid ${COLORS.border}`,
    borderRadius: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    fontFamily: FONTS.ui,
    zIndex: 200,
    minWidth: 360,
    maxWidth: 440,
    // Interactive since E.4 (dismiss + leave buttons) — was 'none' when the
    // overlay was a read-only 15s flash.
    pointerEvents: 'auto',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 24,
    fontWeight: 'bold',
    color: COLORS.textAccent,
    textAlign: 'center',
    margin: 0,
  };

  const winnerStyle: React.CSSProperties = {
    fontSize: 18,
    fontWeight: 'bold',
    color: COLORS.success,
    textAlign: 'center',
    padding: '8px 0',
    borderBottom: `1px solid ${COLORS.borderFaint}`,
  };

  const rowStyle = (isMe: boolean): React.CSSProperties => ({
    padding: '4px 12px',
    fontSize: 14,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: isMe ? COLORS.surfaceHover : 'transparent',
    fontWeight: isMe ? 'bold' : 'normal',
    color: isMe ? COLORS.textAccent : COLORS.textPrimary,
  });

  const localSid: string | null = (globalThis as any).nm?.getRoom?.()?.sessionId ?? null;
  const winner = sorted[0];
  const winnerName = winner?.heroName ?? '—';
  // Force-ended winners have no finish time — show DNF, never "0.0s".
  const winnerTime = winner?.finishTimeMs
    ? formatRaceTime(winner.finishTimeMs)
    : winner?.status === 'dnf' ? 'DNF' : '—';

  return (
    <div style={containerStyle}>
      <button
        data-testid="race-results-dismiss"
        onClick={clearLastEnded}
        style={{
          position: 'absolute',
          top: 8,
          right: 10,
          background: 'transparent',
          border: 'none',
          color: COLORS.textMuted,
          fontSize: 16,
          cursor: 'pointer',
          padding: 2,
        }}
        title="Dismiss results"
      >
        ✕
      </button>
      <h2 style={titleStyle}>🏁 Race Results</h2>
      <div style={winnerStyle}>
        🏆 {winnerName} — {winnerTime}
      </div>
      <div style={{ maxHeight: 240, overflowY: 'auto' }}>
        {sorted.length === 0 ? (
          <div style={{ padding: '8px 12px', color: COLORS.textMuted, fontSize: 13 }}>
            No finishers.
          </div>
        ) : (
          sorted.map((entry, i) => {
            const isMe = entry.sessionId === localSid;
            const place = entry.place ?? i + 1;
            const time = entry.finishTimeMs;
            const dnf = entry.status === 'dnf';
            return (
              <div key={entry.sessionId} style={rowStyle(isMe)}>
                <span>#{place} {entry.heroName}{isMe ? ' (You)' : ''}</span>
                <span style={{ color: dnf ? COLORS.danger : COLORS.textSecondary }}>
                  {dnf ? 'DNF' : time ? formatRaceTime(time) : '—'}
                </span>
              </div>
            );
          })
        )}
      </div>
      <button
        data-testid="race-results-leave"
        onClick={leaveToHub}
        style={{
          alignSelf: 'center',
          padding: '6px 16px',
          borderRadius: 6,
          border: `1px solid ${COLORS.borderFaint}`,
          background: COLORS.surfaceRaised,
          color: COLORS.textPrimary,
          fontSize: 12,
          cursor: 'pointer',
        }}
      >
        🏠 Leave to Lobby
      </button>
    </div>
  );
};
